import React, { ErrorInfo, ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

console.log("[System] JS Bundle Loaded. Starting React Initialization...");

// Watchdog: If React doesn't mount in 3 seconds, throw an error to show the UI
const mountTimeout = setTimeout(() => {
  const root = document.getElementById('root');
  if (root && !root.hasAttribute('data-react-mounted')) {
    console.error("[System] Mount Timeout: React failed to take over DOM within 3000ms.");
    // This console.error will be caught by our index.html logger and shown to user
    throw new Error("Application Mount Timeout - Check Console Logs");
  }
}, 3000);

// Error Boundary Component to catch runtime errors INSIDE React
interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[React] Component Error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
          <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg border border-red-200 overflow-hidden">
            <div className="bg-red-50 px-6 py-4 border-b border-red-100 flex items-center justify-between">
              <h1 className="text-xl font-bold text-red-700">System Error Detected</h1>
              <span className="text-xs font-mono text-red-500 bg-white px-2 py-1 rounded border border-red-200">ERR_RUNTIME</span>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-slate-600">
                The application encountered an unexpected issue and needs to restart.
              </p>
              {this.state.error && (
                <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto max-h-64">
                  <code className="text-red-400 font-mono text-sm block mb-2">
                    {this.state.error.toString()}
                  </code>
                  {this.state.errorInfo && (
                     <pre className="text-slate-500 text-xs font-mono whitespace-pre-wrap">
                       {this.state.errorInfo.componentStack}
                     </pre>
                  )}
                </div>
              )}
              <div className="flex justify-end pt-2">
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  Reload Application
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Application Mount Logic
const rootElement = document.getElementById('root');

if (rootElement) {
  console.log("[System] Root element found. Mounting React...");
  
  try {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </React.StrictMode>
    );
    // Mark root as mounted so HTML error trap doesn't overwrite if a later async error occurs
    rootElement.setAttribute('data-react-mounted', 'true');
    clearTimeout(mountTimeout); // Clear the watchdog
    console.log("[System] React render command issued successfully.");
  } catch (e) {
    console.error("[System] Failed to create React root:", e);
    // Rethrow to let the HTML global handler catch it and show the UI
    throw e;
  }
} else {
  console.error("[System] FATAL: Root element with id 'root' not found.");
}